import { useMemo, useState } from "react";
import { Plus, X } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormActions } from "@jbnl/shared-ui";
import {
  useSetStandaardSpeelschema,
  useStandaardSpeelschemas,
  type KlasseStam,
  type NiveauStam,
  type StandaardRegel,
} from "./useStamgegevens";

const uurEnMinuut = (time: string) => time.slice(0, 5);

/**
 * `BezettingGrid`'s pattern: typing does not save, because several rows only mean anything
 * together (ADR-0039). Editing never touches a baan already made from it.
 */
export function StandaardSpeelschemaGrid({
  klasse,
  niveau,
}: {
  klasse: KlasseStam;
  niveau: NiveauStam;
}) {
  const { t } = useTranslation(["stamgegevens", "common"]);
  const { data: speelschemas, isPending } = useStandaardSpeelschemas();
  const opslaan = useSetStandaardSpeelschema();

  // What the server holds. An absent pair and an empty grid are the same thing to this screen:
  // both mean a baan of this klasse at this niveau is created with no rows at all.
  const opgeslagen = useMemo<StandaardRegel[]>(
    () =>
      speelschemas
        ?.find((s) => s.klasseId === klasse.id && s.niveauId === niveau.id)
        ?.regels.map((r) => ({ time: uurEnMinuut(r.time), label: r.label })) ??
      [],
    [speelschemas, klasse.id, niveau.id],
  );

  // `null` reads through to the server, so a refetch lands without an effect to re-seed from.
  const [bewerkt, setBewerkt] = useState<StandaardRegel[] | null>(null);
  const regels = bewerkt ?? opgeslagen;

  const vuil =
    bewerkt !== null && JSON.stringify(bewerkt) !== JSON.stringify(opgeslagen);
  const beschikbaar = regels.filter((r) => !r.label).length;

  function wijzig(index: number, verandering: Partial<StandaardRegel>) {
    setBewerkt(
      regels.map((r, i) => (i === index ? { ...r, ...verandering } : r)),
    );
  }

  function bewaar() {
    opslaan.mutate(
      {
        klasseId: klasse.id,
        niveauId: niveau.id,
        regels: [...regels].sort((a, b) => a.time.localeCompare(b.time)),
      },
      { onSuccess: () => setBewerkt(null) },
    );
  }

  if (isPending) {
    return (
      <p className="text-sm text-muted-foreground">{t("common:table.busy")}</p>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        <Trans
          t={t}
          i18nKey="speelschemas.uitleg"
          components={{ em: <em /> }}
        />
      </p>

      {regels.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t("speelschemas.leeg")}
        </p>
      ) : (
        <ul className="space-y-2">
          {regels.map((regel, index) => (
            <li key={index} className="flex items-center gap-2">
              <Input
                type="time"
                className="w-32"
                value={regel.time}
                aria-label={t("speelschemas.tijdVeld", {
                  nummer: index + 1,
                  klasse: klasse.name,
                  niveau: niveau.name,
                })}
                onChange={(event) =>
                  wijzig(index, { time: event.target.value })
                }
              />
              {/* One input, and an empty one means the row is available for a ploeg. Free text
                  rather than a Pauze-checkbox: the published season prints a Herdenking too. */}
              <Input
                value={regel.label ?? ""}
                maxLength={60}
                placeholder={t("speelschemas.vrij")}
                aria-label={t("speelschemas.labelVeld", {
                  nummer: index + 1,
                  klasse: klasse.name,
                  niveau: niveau.name,
                })}
                onChange={(event) =>
                  wijzig(index, { label: event.target.value || null })
                }
              />
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={t("speelschemas.regelWeghalen", {
                  nummer: index + 1,
                })}
                onClick={() => setBewerkt(regels.filter((_, i) => i !== index))}
              >
                <X />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="secondary"
          size="sm"
          onClick={() =>
            setBewerkt([...regels, { time: volgendeTijd(regels), label: null }])
          }
        >
          <Plus />
          {t("speelschemas.regelToevoegen")}
        </Button>

        {/* The number the reglement's maxima actually live in: capacity is the count of rows a
            ploeg may stand in, and it is stored nowhere else. */}
        <span className="text-sm text-muted-foreground">
          {t("speelschemas.plekken", { count: beschikbaar })}
        </span>
      </div>

      <SpeelschemaOpslaanActies
        klasse={klasse}
        vuil={vuil}
        opslaan={opslaan}
        onBewaar={bewaar}
      />
    </div>
  );
}

/**
 * The klasse is in the label because four of these buttons sit on one niveau, one per klasse, and
 * a row of identical "Opslaan" buttons is a row you have to count.
 */
function SpeelschemaOpslaanActies({
  klasse,
  vuil,
  opslaan,
  onBewaar,
}: {
  klasse: KlasseStam;
  vuil: boolean;
  opslaan: ReturnType<typeof useSetStandaardSpeelschema>;
  onBewaar: () => void;
}) {
  const { t } = useTranslation(["stamgegevens", "common"]);

  return (
    <FormActions>
      {opslaan.error && (
        <p className="text-sm text-destructive">{opslaan.error.message}</p>
      )}
      {vuil && !opslaan.isPending && (
        <span className="text-sm text-muted-foreground">
          {t("speelschemas.vuil")}
        </span>
      )}
      <Button onClick={onBewaar} disabled={!vuil || opslaan.isPending}>
        {opslaan.isPending
          ? t("common:actions.busy")
          : t("speelschemas.opslaan", { klasse: klasse.name })}
      </Button>
    </FormActions>
  );
}

/**
 * Half an hour after the last: the commonest interval JBNL runs, and a starting point rather
 * than a rule - deriving one is the arithmetic ADR-0039 rejected.
 */
function volgendeTijd(regels: StandaardRegel[]) {
  const laatste = [...regels]
    .sort((a, b) => a.time.localeCompare(b.time))
    .at(-1);
  if (!laatste) {
    return "08:30";
  }

  const [uur, minuut] = laatste.time.split(":").map(Number);
  const totaal = Math.min(uur * 60 + minuut + 30, 24 * 60 - 1);

  return `${String(Math.floor(totaal / 60)).padStart(2, "0")}:${String(totaal % 60).padStart(2, "0")}`;
}
